import { MenuItem, MenuSection, Header, Heading, Text, ActionButton, MenuTrigger, Menu, Tooltip, TooltipTrigger } from "@react-spectrum/s2";
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { Key } from 'react';
import { Leave as LogOut, MenuHamburger as ShowMenu, Checkmark } from '../icons/s2';
import SpeedFast from "@react-spectrum/s2/icons/SpeedFast";
import Lightbulb from "@react-spectrum/s2/icons/Lightbulb";
import Contrast from "@react-spectrum/s2/icons/Contrast";
import Lighten from "@react-spectrum/s2/icons/Lighten";
import { ImsUserProfile, Permission, hasPermission } from '../../types';
import { useThemeMode } from '../../contexts/ThemeModeContext';
import { AdobeLogo } from '../common/AdobeLogo';
import { OrgSwitcher, buildOrgItems } from '../common/OrgSwitcher';
import { APP_NAME } from '../../constants';
import './Navbar.css';
interface NavbarProps {
    user: ImsUserProfile | null;
    isAuthenticated: boolean;
    onLogout: () => void;
    onStartTour?: () => void;
    orgName?: string | null;
    userOrgIds?: string[];
    orgNames?: Record<string, string>;
    selectedOrgId?: string | null;
    onOrgSwitch?: (orgId: string) => void;
    onHomeClick?: () => void;
    orgPermissions?: string[];
    onCommandCenter?: () => void;
    /**
   * When provided, renders a leading hamburger button that toggles the
   * off-canvas navigation drawer. Only supplied by the authenticated app shell
   * at the mobile breakpoint; omitted (button hidden) on desktop.
   */ onToggleNav?: () => void;
    /** Reflects the drawer open state for the hamburger's `aria-expanded`. */ isNavOpen?: boolean;
    /**
   * Selects what the leading hamburger controls. `'drawer'` (default, mobile)
   * toggles the off-canvas navigation drawer; `'collapse'` (desktop) toggles
   * the primary rail between full and icon-only. Only the accessible label
   * differs — the same hamburger glyph and slot are reused in both cases so the
   * control stays consistent in the top-left. For `'collapse'`, `isNavOpen`
   * means "rail expanded" (full view).
   */ navToggleVariant?: 'drawer' | 'collapse';
    /**
   * Whether to show the organization switcher in the top bar. The app shell
   * passes `false` at the mobile breakpoint, where the switcher is relocated
   * into the navigation drawer so it never overflows / overlaps the brand on a
   * narrow bar. Defaults to `true` (desktop and standalone gate screens).
   */ showOrgPicker?: boolean;
}
function getInitials(user: ImsUserProfile): string {
    if (user.first_name && user.last_name) {
        return `${user.first_name[0]}${user.last_name[0]}`.toUpperCase();
    }
    if (user.displayName) {
        const parts = user.displayName.trim().split(/\s+/);
        return parts.length >= 2 ? `${parts[0][0]}${parts[1][0]}`.toUpperCase() : parts[0][0].toUpperCase();
    }
    if (user.email) return user.email[0].toUpperCase();
    return 'U';
}
const PROFILE_ACTION_COMMAND_CENTER = 'command-center';
const PROFILE_ACTION_LOGOUT = 'logout';
const PROFILE_ACTION_ORG_CONTEXT = 'org-context';
/** Prefix for profile-menu org-switch items, so their action keys never
 *  collide with the static action items (command center / logout). */ const ORG_OPTION_KEY_PREFIX = 'org:';
export const Navbar: React.FC<NavbarProps> = ({ user, isAuthenticated, onLogout, onStartTour, orgName, userOrgIds = [], orgNames = {}, selectedOrgId, onOrgSwitch, onHomeClick, orgPermissions, onCommandCenter, onToggleNav, isNavOpen = false, navToggleVariant = 'drawer', showOrgPicker = true })=>{
    const { mode, toggle } = useThemeMode();
    const { t } = useTranslation();
    // One hamburger slot, two behaviours: the drawer variant (mobile) opens/closes
    // the off-canvas nav; the collapse variant (desktop) expands/collapses the
    // primary rail. `isNavOpen` means "drawer open" or "rail expanded" respectively.
    const navToggleLabel = navToggleVariant === 'collapse' ? isNavOpen ? t('nav.collapseNav') : t('nav.expandNav') : isNavOpen ? t('nav.closeNav') : t('nav.openNav');
    const displayName = user?.displayName || user?.email || t('nav.userFallback');
    const showCommandCenter = hasPermission(orgPermissions, Permission.GLOBAL_ADMIN) && Boolean(onCommandCenter);
    // Below the mobile breakpoint the header org switcher is hidden; the full org
    // selector is surfaced inside the profile menu instead (same list + handler).
    const showOrgContext = !showOrgPicker && Boolean(orgName);
    const hasMultipleOrgs = userOrgIds.length > 1 && Boolean(onOrgSwitch);
    const orgMenuItems = useMemo(()=>buildOrgItems({
            userOrgIds,
            orgNames,
            selectedOrgId,
            orgName
        }), [
        userOrgIds,
        orgNames,
        selectedOrgId,
        orgName
    ]);
    const handleProfileAction = useCallback((key: Key)=>{
        const action = String(key);
        if (action.startsWith(ORG_OPTION_KEY_PREFIX)) {
            const orgId = action.slice(ORG_OPTION_KEY_PREFIX.length);
            if (orgId !== selectedOrgId && onOrgSwitch) onOrgSwitch(orgId);
            return;
        }
        if (action === PROFILE_ACTION_COMMAND_CENTER && onCommandCenter) {
            onCommandCenter();
        } else if (action === PROFILE_ACTION_LOGOUT) {
            onLogout();
        }
    }, [
        onCommandCenter,
        onLogout,
        onOrgSwitch,
        selectedOrgId
    ]);
    return (<div className="app-navbar">
      <div className={"app-navbar__row" + " sd171 _Ce171 eb171 Za171"}>
        <div className={"app-navbar__left" + " sd171 eb171 Ug171 qg171"}>
          {onToggleNav && (<ActionButton isQuiet onPress={onToggleNav} aria-label={navToggleLabel} aria-expanded={isNavOpen} UNSAFE_className="app-navbar__nav-toggle">
              <ShowMenu/>
            </ActionButton>)}
          {onHomeClick ? (<button type="button" onClick={onHomeClick} className="navbar-brand navbar-brand--button" aria-label={t('nav.goHome')}>
              <AdobeLogo width={33} height={32} style={{
        display: 'block',
        flexShrink: 0
    }}/>
              <Heading level={1} UNSAFE_className="app-navbar__title" styles={" Jy171 Gy171 Iy171 Hy171"} UNSAFE_style={{
        fontSize: '16px',
        fontWeight: 700,
        letterSpacing: '-0.01em'
    }}>
                {APP_NAME}
              </Heading>
            </button>) : (<div className="navbar-brand">
              <AdobeLogo width={33} height={32} style={{
        display: 'block',
        flexShrink: 0
    }} title={t('nav.logoTitle')}/>
              <Heading level={1} UNSAFE_className="app-navbar__title" styles={" Jy171 Gy171 Iy171 Hy171"} UNSAFE_style={{
        fontSize: '16px',
        fontWeight: 700,
        letterSpacing: '-0.01em'
    }}>
                {APP_NAME}
              </Heading>
            </div>)}
        </div>
        <div className={"app-navbar__right" + " sd171 eb171 Ue171 qe171"}>
          {isAuthenticated && user && (<>
              {showOrgPicker && (orgName || selectedOrgId) ? (<OrgSwitcher orgName={orgName} userOrgIds={userOrgIds} orgNames={orgNames} selectedOrgId={selectedOrgId} onOrgSwitch={onOrgSwitch}/>) : null}

            {showOrgPicker && (<span className="app-navbar__divider" aria-hidden="true"/>)}

            {onStartTour && (<TooltipTrigger delay={0}>
                <ActionButton isQuiet onPress={onStartTour} aria-label={t('nav.productTour')} UNSAFE_className="app-navbar__tour-btn">
                  <Lightbulb/>
                </ActionButton>
                <Tooltip>{t('nav.productTour')}</Tooltip>
              </TooltipTrigger>)}
            </>)}
          <ActionButton isQuiet onPress={toggle} aria-label={mode === 'dark' ? t('nav.themeToLight') : t('nav.themeToDark')} UNSAFE_className="app-navbar__theme-toggle">
            {mode === 'dark' ? <Lighten/> : <Contrast/>}
          </ActionButton>
          {isAuthenticated && user && (<MenuTrigger>
              <ActionButton isQuiet aria-label={t('nav.openProfileMenu')} UNSAFE_className="navbar-profile-trigger">
                <span className="navbar-profile-avatar" aria-hidden="true">
                  {getInitials(user)}
                </span>
              </ActionButton>
              <Menu onAction={handleProfileAction} disabledKeys={showOrgContext && !hasMultipleOrgs ? [
        PROFILE_ACTION_ORG_CONTEXT
    ] : []}>
                {showOrgContext ? (<MenuSection>
                  <Header><Heading>{t('nav.orgSectionHeading')}</Heading></Header>
                    {hasMultipleOrgs ? orgMenuItems.map((item)=>(<MenuItem id={`${ORG_OPTION_KEY_PREFIX}${item.key}`} textValue={item.label} key={`${ORG_OPTION_KEY_PREFIX}${item.key}`}>
                            {item.key === selectedOrgId ? (<Checkmark size="S"/>) : null}
                            <Text UNSAFE_className="navbar-profile-menu-org-name">
                              {item.label}
                            </Text>
                          </MenuItem>)) : (<MenuItem id={PROFILE_ACTION_ORG_CONTEXT} textValue={orgName ?? t('nav.orgSectionHeading')}>
                            <Text UNSAFE_className="navbar-profile-menu-org-name">
                              {orgName}
                            </Text>
                          </MenuItem>)}
                  </MenuSection>) : null}
                {showCommandCenter ? (<MenuSection>
                  <Header><Heading>{t('nav.greeting', {
        name: displayName
    })}</Heading></Header>
                    <MenuItem id={PROFILE_ACTION_COMMAND_CENTER} textValue={t('nav.commandCenterValue')}>
                      <SpeedFast/>
                      <Text>{t('nav.commandCenter')}</Text>
                    </MenuItem>
                    <MenuItem id={PROFILE_ACTION_LOGOUT} textValue={t('nav.logout')}>
                      <LogOut size="S" UNSAFE_className="navbar-profile-menu-logout-icon"/>
                      <Text UNSAFE_className="navbar-profile-menu-logout-text">{t('nav.logout')}</Text>
                    </MenuItem>
                  </MenuSection>) : (<MenuSection>
                  <Header><Heading>{t('nav.greeting', {
        name: displayName
    })}</Heading></Header>
                    <MenuItem id={PROFILE_ACTION_LOGOUT} textValue={t('nav.logout')}>
                      <LogOut size="S" UNSAFE_className="navbar-profile-menu-logout-icon"/>
                      <Text UNSAFE_className="navbar-profile-menu-logout-text">{t('nav.logout')}</Text>
                    </MenuItem>
                  </MenuSection>)}
              </Menu>
            </MenuTrigger>)}
        </div>
      </div>
    </div>);
};
export default Navbar;

import "macro-65f9217a708757627ceef10a35da4fee8d79c48df55268f3055c4a53a2582d7a.css";
import "macro-4da9f00f1a71c4b96285373a57f57bd29923a0b819297108f6f51ab8bfcc767e.css";
import "macro-9265ce547da9b2dc6ffe8b0a3109d3facb0513bbd657f7ae9beeb5d91b71740f.css";
import "macro-9265ce547da9b2dc6ffe8b0a3109d3facb0513bbd657f7ae9beeb5d91b71740f.css";
import "macro-670689c431bc3bbe3dd9c9217c99a533309a8e20cb86cb029d05484a86ca10f4.css";